"use client";

import { X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { Typography } from "@/components/shared/typography/typography";
import { getCheckboxSelection } from "./appliedFilterUtils";
import { MarketplaceGroupTourFiltersBody } from "./MarketplaceGroupTourFiltersBody";
import { useIsMobileViewport } from "./useIsMobileViewport";
import { getMarketplaceModalFilters } from "@/lib/marketplace/get-modal-filters";
import type {
  MarketplaceDynamicFilter,
  MarketplaceFilterValue,
  MarketplacePriceFilterValue,
  MarketplaceSelectedFilters,
} from "@/types/api/marketplace";

const filterModalCloseButtonClass =
  "flex cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white p-1.5 text-gray-900";

interface FiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: MarketplaceDynamicFilter[];
  selectedFilters: MarketplaceSelectedFilters;
  onFilterChange: (
    type: string,
    value: MarketplaceFilterValue | undefined
  ) => void;
  onClearAll: () => void;
}

export function FiltersModal({
  open,
  onOpenChange,
  filters,
  selectedFilters,
  onFilterChange,
  onClearAll,
}: FiltersModalProps) {
  const isMobile = useIsMobileViewport();
  const modalFilters = getMarketplaceModalFilters(filters);

  const activeFilterCount = modalFilters.reduce((total, filter) => {
    const value = selectedFilters[filter.type];
    if (Array.isArray(value)) return total + value.length;
    if (value && typeof value === "object") {
      return (
        total +
        Object.values(value as Record<string, string | undefined>).filter(
          Boolean
        ).length
      );
    }
    return total;
  }, 0);

  const handleCheckboxToggle = (type: string, optionId: number) => {
    const current = getCheckboxSelection(selectedFilters, type);
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    onFilterChange(type, next.length > 0 ? next : undefined);
  };

  const handlePriceChange = (
    type: string,
    field: "min" | "max",
    raw: string
  ) => {
    const current =
      (selectedFilters[type] as MarketplacePriceFilterValue) ?? {};
    const next = { ...current, [field]: raw || undefined };
    const hasValue = next.min || next.max;
    onFilterChange(type, hasValue ? next : undefined);
  };

  const handlePriceFieldRemove = (type: string, field: "min" | "max") => {
    const current =
      (selectedFilters[type] as MarketplacePriceFilterValue) ?? {};
    const next = { ...current, [field]: undefined };
    const hasValue = next.min || next.max;
    onFilterChange(type, hasValue ? next : undefined);
  };

  const filterBodyProps = {
    modalFilters,
    selectedFilters,
    onCheckboxToggle: handleCheckboxToggle,
    onPriceChange: handlePriceChange,
    onPriceFieldRemove: handlePriceFieldRemove,
  };

  const clearAllClassName = `font-semibold underline ${
    activeFilterCount > 0 ? "text-gray-900" : "text-gray-400"
  }`;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex h-[85vh] flex-col bg-white p-0 lg:hidden [&>button]:hidden">
          <SheetTitle className="flex shrink-0 items-center justify-between bg-white px-4 pt-3">
            <button
              type="button"
              onClick={onClearAll}
              disabled={activeFilterCount === 0}
              className={`text-md font-family-body cursor-pointer ${clearAllClassName}`}>
              Clear all
            </button>
            <Typography
              variant="p"
              className="align-text-top text-lg font-semibold text-gray-900">
              Filter
            </Typography>
            <SheetClose
              className={filterModalCloseButtonClass}
              aria-label="Close">
              <X className="h-4 w-4" />
            </SheetClose>
          </SheetTitle>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <MarketplaceGroupTourFiltersBody {...filterBodyProps} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[min(600px,90vh)] w-full max-w-lg flex-col gap-0 overflow-hidden rounded-2xl border-gray-200 bg-white p-0 shadow-lg sm:max-w-lg">
        <div className="relative shrink-0 border-b border-gray-200 px-4 py-4">
          <DialogTitle className="text-center text-lg font-bold text-gray-900">
            Filters
          </DialogTitle>
          <DialogClose
            className={`${filterModalCloseButtonClass} absolute top-4 right-4 z-10`}
            aria-label="Close filters">
            <X className="h-4 w-4" />
          </DialogClose>
        </div>

        <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto">
          <MarketplaceGroupTourFiltersBody {...filterBodyProps} />
        </div>

        <div className="flex shrink-0 items-center border-t border-gray-200 px-4 py-4">
          <button
            type="button"
            onClick={onClearAll}
            disabled={activeFilterCount === 0}
            className={`cursor-pointer text-sm font-medium ${clearAllClassName}`}>
            Clear all
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
